/** @jsxImportSource preact */
// Loaded only by the public /calculators/<slug>/ pages: mounts the same calculator the planner uses, live, without the
// planner's state, worker or storage. Starting figures come from the household saved in this browser when there is
// one (same origin, same storage), else from the example. Nothing is fetched except the site's own market data.
import { render } from 'preact';
import { useState } from 'preact/hooks';
import { EXAMPLE_HOUSEHOLD, normaliseHousehold, type Household, type MarketSnapshot } from '@iht/engine';
import { loadJSON, hasKey, KEYS } from './store';
import { loadMarket } from './market';
import { giltTable, ensureGiltTable } from './gilts';
import { CALCULATORS } from './calculators';
import { DEFS, type Values } from './calculators/defs';
import { CalcForm, setPrefill } from './components/results/CalcForm';

function household(): { h: Household; saved: boolean } {
  try {
    if (hasKey(KEYS.household)) return { h: normaliseHousehold(loadJSON<unknown>(KEYS.household, null)), saved: true };
  } catch { /* unreadable or absent */ }
  return { h: structuredClone(EXAMPLE_HOUSEHOLD), saved: false };
}

function Page({ calcKey: key, snapshot }: { calcKey: string; snapshot: MarketSnapshot | null }) {
  const meta = CALCULATORS.find(c => c.key === key)!;
  const { h, saved } = household();
  const [vals, setVals] = useState<Values>(() => {
    const d = DEFS[key]!, v = d.defaults(h);
    // a link can preset any field: /calculators/cost-of-fees/?charges=0.22
    try { for (const [k, raw] of new URLSearchParams(location.search)) { const f = d.fields.find(x => x.key === k); if (!f) continue; if (f.kind === 'toggle') v[k] = raw === 'true' || raw === '1'; else if (f.kind === 'select') v[k] = raw; else { const num = parseFloat(raw); if (Number.isFinite(num)) v[k] = num; } } } catch { /* no URL API */ }
    return v;
  });
  const carryOn = () => { setPrefill(key, vals); location.href = `/plan/#calc=${key}`; };
  return (
    <>
      <p class="meta" style="margin:0 0 10px">{saved ? 'Starting from the figures saved in your planner on this device.' : 'Starting from an example; change any figure.'} Nothing you type leaves this page.</p>
      <CalcForm meta={meta} initial={vals} snapshot={snapshot} gilts={giltTable.value} onNeedGilts={ensureGiltTable} onChange={setVals} showLede={false}
        footer={() => <p style="margin:6px 0 0"><button class="btn primary" type="button" onClick={carryOn}>Carry on in the planner with these figures</button></p>} />
    </>
  );
}

const root = document.getElementById('calc-live');
const key = root?.dataset.calc;
if (root && key && DEFS[key]) {
  // the gilt table is a signal: the two gilt calculators re-render on their own when it arrives
  const mount = (snapshot: MarketSnapshot | null) => render(<Page calcKey={key} snapshot={snapshot} />, root);
  mount(null);
  void loadMarket().then(m => { if (m) mount(m); });
}
